import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { MechanicService } from '../../services/mechanicService';
import { Wrench, Clock, CheckCircle, AlertCircle, Phone, MapPin, Car } from 'lucide-react';

const MechanicRequests = () => {
  const user = JSON.parse(localStorage.getItem('roadserve_current_user') || '{}');
  const [filterStatus, setFilterStatus] = useState('all');

  const { data: requests = [], isLoading, refetch } = useQuery({
    queryKey: ['mechanicRequests', user?.id],
    queryFn: () => MechanicService.getAssignedRequests(user?.id),
    enabled: !!user?.id,
  });

  const handleStatusUpdate = async (requestId, newStatus) => {
    const result = await MechanicService.updateRequestStatus(requestId, newStatus);
    if (result.success) {
      alert('Status updated successfully!');
      refetch();
    } else {
      alert('Failed to update status: ' + result.error);
    }
  };

  const filteredRequests = filterStatus === 'all' 
    ? requests 
    : requests.filter(r => r.status?.toLowerCase() === filterStatus.toLowerCase());

  const getStatusColor = (status) => {
    const s = status?.toLowerCase() || '';
    if (s === 'completed') return 'bg-green-100 text-green-700';
    if (s === 'in_progress') return 'bg-blue-100 text-blue-700';
    if (s === 'assigned') return 'bg-yellow-100 text-yellow-700';
    return 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Service Requests</h1>
          <p className="text-gray-600 mt-1">View and manage your assigned requests</p>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={() => setFilterStatus('all')}
            className={`px-4 py-2 rounded-lg border ${
              filterStatus === 'all' ? 'bg-orange-500 text-white border-orange-500' : 'bg-white text-gray-700 border-gray-300'
            }`}
          >
            All
          </button>
          <button
            onClick={() => setFilterStatus('assigned')}
            className={`px-4 py-2 rounded-lg border ${
              filterStatus === 'assigned' ? 'bg-orange-500 text-white border-orange-500' : 'bg-white text-gray-700 border-gray-300'
            }`}
          >
            Assigned
          </button>
          <button
            onClick={() => setFilterStatus('in_progress')}
            className={`px-4 py-2 rounded-lg border ${
              filterStatus === 'in_progress' ? 'bg-orange-500 text-white border-orange-500' : 'bg-white text-gray-700 border-gray-300'
            }`}
          >
            In Progress
          </button>
          <button
            onClick={() => setFilterStatus('completed')}
            className={`px-4 py-2 rounded-lg border ${
              filterStatus === 'completed' ? 'bg-orange-500 text-white border-orange-500' : 'bg-white text-gray-700 border-gray-300'
            }`}
          >
            Completed
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="bg-white rounded-xl p-12 shadow-sm border border-gray-200 text-center">
          <div className="w-16 h-16 border-4 border-orange-500 border-t-transparent rounded-full spinner mx-auto mb-4"></div>
          <p className="text-gray-600">Loading requests...</p>
        </div>
      ) : filteredRequests.length === 0 ? (
        <div className="bg-white rounded-xl p-12 shadow-sm border border-gray-200 text-center">
          <Wrench size={48} className="mx-auto text-gray-400 mb-4" />
          <p className="text-gray-500">No requests found</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredRequests.map((request) => (
            <div key={request.id} className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="text-lg font-semibold text-gray-900">Request #{request.id?.slice(0, 8)}</h3>
                    <span className={`px-2 py-1 text-xs font-medium rounded ${getStatusColor(request.status)}`}>
                      {request.status || 'Assigned'}
                    </span>
                  </div>
                  <p className="text-gray-600 mb-3">{request.problem || request.description || 'No description'}</p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    {request.customer_name && (
                      <div>
                        <p className="text-gray-500">Customer</p>
                        <p className="font-medium text-gray-900">{request.customer_name}</p>
                      </div>
                    )}
                    {request.contact_number && (
                      <div>
                        <p className="text-gray-500">Contact</p>
                        <a href={`tel:${request.contact_number}`} className="font-medium text-orange-600 hover:underline flex items-center space-x-1">
                          <Phone size={14} />
                          <span>{request.contact_number}</span>
                        </a>
                      </div>
                    )}
                    {request.location && (
                      <div>
                        <p className="text-gray-500">Location</p>
                        <p className="font-medium text-gray-900 flex items-center space-x-1">
                          <MapPin size={14} />
                          <span>{request.location}</span>
                        </p>
                      </div>
                    )}
                    {request.vehicle_number && (
                      <div>
                        <p className="text-gray-500">Vehicle</p>
                        <p className="font-medium text-gray-900 flex items-center space-x-1">
                          <Car size={14} />
                          <span>{request.vehicle_number}</span>
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-end space-x-2 pt-4 border-t border-gray-200">
                {request.status === 'assigned' && (
                  <button
                    onClick={() => handleStatusUpdate(request.id, 'in_progress')}
                    className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center space-x-2"
                  >
                    <Clock size={18} />
                    <span>Start Work</span>
                  </button>
                )}
                {request.status === 'in_progress' && (
                  <button
                    onClick={() => handleStatusUpdate(request.id, 'completed')}
                    className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 flex items-center space-x-2"
                  >
                    <CheckCircle size={18} />
                    <span>Mark Complete</span>
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MechanicRequests;
